import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarCheck, Loader2, Snowflake } from 'lucide-react';
import { Button } from '@/components/Button';
import { Skeleton } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { formatarDataHora } from '@/lib/format';
import { NomeDePessoa, useDicionarioDePessoas } from '@/features/tickets/pessoas';
import { chaves } from '@/features/tickets/tickets.api';
import { AvisoDeFalha } from '@/features/workflow/componentes';
import { AprovacaoPorId } from '@/features/workflow/PainelDeAprovacoes';
import { classificarFalha, type Falha } from '@/features/workflow/recusa';
import { rotuloDeHorario } from '@/features/changes/calendario';
import {
  agendarMudanca,
  chavesDeMudanca,
  listarCongelamentos,
  listarJanelas,
  pedirExcecaoDeCongelamento,
  preverAgendamento,
} from '@/features/changes/changes.api';
import {
  AvisoDeConflito,
  Campo,
  CLASSE_DE_CAMPO,
  EtiquetaDeExcecao,
  EtiquetaDeJanela,
  Secao,
} from '@/features/changes/componentes';
import {
  congelamentosBloqueando,
  congelamentosSemExcecao,
} from '@/features/changes/conflitos';
import {
  ROTULO_DE_TIPO_DE_CONFLITO,
  type Congelamento,
  type Conflito,
  type Mudanca,
} from '@/features/changes/tipos';

/**
 * Agendar a mudança numa janela — critérios 4.3 e 4.12.
 *
 * ## A recusa aparece antes do clique
 *
 * `GET …/schedule/preview` responde "o que aconteceria se eu agendasse aqui?"
 * sem gravar nada. A tela chama essa rota **enquanto** a pessoa escolhe a
 * janela no combo, de modo que o congelamento apareça antes do botão, e não
 * como um 422 depois dele.
 *
 * ## As duas metades da regra, lado a lado
 *
 *   • **colisão com outra mudança não impede.** Ela vai para a tela do comitê,
 *     porque duas mudanças no mesmo servidor na mesma noite é o que uma versão
 *     de entrega faz de propósito;
 *   • **congelamento impede.** A saída é uma exceção aprovada por autoridade
 *     superior — e essa aprovação é a mesma máquina da Fase 3, não uma segunda.
 *
 * ## O bloqueio vem de duas fontes, e a tela precisa das duas
 *
 * `mudanca.blocking` é do ponto de vista da janela em que a mudança **já está**
 * — vazio, portanto, enquanto ela não tiver janela. Quem sabe que a janela
 * escolhida no combo cai em congelamento é `previa.data.blocking`. As duas
 * entram na conta de `congelamentosBloqueando`, senão o formulário de exceção
 * não aparece justamente no caminho normal: escolher a janela, ver o vermelho,
 * e não ter como pedir a exceção que o cabeçalho promete.
 */
export function AgendamentoDaMudanca({ mudanca }: { mudanca: Mudanca }) {
  const cliente = useQueryClient();
  const [escolhida, setEscolhida] = useState('');
  const [falha, setFalha] = useState<Falha | null>(null);

  /**
   * O que o botão "Recarregar" de um 409 faz.
   *
   * Recarrega a subárvore inteira do chamado — ficha, prévia e transições —
   * porque um conflito de versão significa que **algo** mudou, e trazer só a
   * mudança deixaria a prévia respondendo sobre o estado anterior. Sem isto, e
   * com `staleTime` de 30 s, cada nova tentativa reenviaria a mesma versão
   * obsoleta e receberia o mesmo 409, sem outro caminho além do F5.
   */
  const recarregar = () => void cliente.refetchQueries({ queryKey: chaves.item(mudanca.ticketId) });

  const janelas = useQuery({ queryKey: chavesDeMudanca.janelas, queryFn: listarJanelas });

  const previa = useQuery({
    queryKey: chavesDeMudanca.previa(mudanca.ticketId, escolhida),
    queryFn: () => preverAgendamento(mudanca.ticketId, escolhida),
    enabled: escolhida !== '',
  });

  const agendar = useMutation({
    mutationFn: () => agendarMudanca(mudanca.ticketId, escolhida, mudanca.version),
    onMutate: () => setFalha(null),
    onSuccess: () => {
      setEscolhida('');
      // Agendar muda o guard `change.blockingConflictCount` do fluxo e o
      // conteúdo do calendário: as duas raízes recarregam.
      void cliente.invalidateQueries({ queryKey: chaves.raiz });
      void cliente.invalidateQueries({ queryKey: chavesDeMudanca.raiz });
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  const disponiveis = (janelas.data ?? []).filter(
    (janela) => janela.status !== 'CANCELLED' && janela.status !== 'COMPLETED',
  );

  return (
    <Secao
      titulo="Janela de mudança"
      descricao="A mudança roda dentro de uma janela compartilhada. Mover a janela move todas as mudanças dentro dela — por isso o horário não se digita aqui."
    >
      {mudanca.changeWindow ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-primary/25 bg-primary/5 px-3 py-2.5 text-sm">
          <CalendarCheck className="h-4 w-4 text-primary" aria-hidden />
          <span className="font-medium">{mudanca.changeWindow.name}</span>
          <EtiquetaDeJanela situacao={mudanca.changeWindow.status} />
          <span className="tabular-nums text-muted-foreground">
            {formatarDataHora(mudanca.changeWindow.startsAt)} ·{' '}
            {rotuloDeHorario(mudanca.changeWindow.startsAt, mudanca.changeWindow.endsAt)}
          </span>
        </div>
      ) : (
        <p className="rounded-md border border-dashed border-border px-3 py-2.5 text-sm text-muted-foreground">
          Sem janela. Os fluxos de mudança padrão e de mudança normal exigem uma para avançar — o
          guard confere `change.changeWindowId`.
        </p>
      )}

      {janelas.isPending ? <Skeleton className="h-9 w-full" /> : null}
      {janelas.isError ? (
        <AvisoDeFalha
          falha={classificarFalha(janelas.error)}
          aoRecarregar={() => void janelas.refetch()}
        />
      ) : null}

      <div className="flex flex-wrap items-end gap-2">
        <Campo rotulo={mudanca.changeWindow ? 'Reagendar para' : 'Agendar em'} htmlFor="janela-alvo">
          <select
            id="janela-alvo"
            value={escolhida}
            onChange={(evento) => {
              setEscolhida(evento.target.value);
              setFalha(null);
            }}
            className={cn(CLASSE_DE_CAMPO, 'min-w-72')}
          >
            <option value="">Escolha uma janela…</option>
            {disponiveis.map((janela) => (
              <option key={janela.id} value={janela.id}>
                {janela.name} · {formatarDataHora(janela.startsAt)} ·{' '}
                {janela._count.changes} mudança(s)
              </option>
            ))}
          </select>
        </Campo>

        <Button
          size="sm"
          disabled={escolhida === '' || previa.isPending || previa.data?.allowed === false}
          loading={agendar.isPending}
          onClick={() => agendar.mutate()}
        >
          Agendar
        </Button>

        {previa.isFetching ? (
          <span className="inline-flex items-center gap-1.5 pb-2 text-xs text-muted-foreground" role="status">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            conferindo a janela…
          </span>
        ) : null}
      </div>

      {previa.isError ? (
        <AvisoDeFalha
          falha={classificarFalha(previa.error)}
          aoRecarregar={() => void previa.refetch()}
        />
      ) : null}

      {previa.data ? (
        <ListaDeConflitos
          conflitos={previa.data.conflicts}
          vazio={
            previa.data.allowed
              ? 'Nenhuma colisão nesta janela. O agendamento passa.'
              : 'Sem colisão registrada, mas o agendamento está bloqueado.'
          }
          prefixo="Se agendar aqui:"
        />
      ) : null}

      {agendar.data && agendar.data.conflicts.length > 0 ? (
        <AvisoDeConflito
          gravidade="ATENCAO"
          titulo={`Agendada com ${agendar.data.conflicts.length} colisão(ões) registrada(s)`}
        >
          <p className="text-sm">
            A colisão não impediu — ela foi para a trilha de auditoria e para o calendário do
            comitê. Se der errado, a pergunta “alguém sabia?” tem resposta.
          </p>
        </AvisoDeConflito>
      ) : null}

      {falha ? <AvisoDeFalha falha={falha} aoRecarregar={recarregar} /> : null}

      <ConflitosDaMudanca mudanca={mudanca} />
      <ExcecoesDeCongelamento
        mudanca={mudanca}
        bloqueiosDaPrevia={previa.data?.blocking ?? []}
        nomeDaJanelaEscolhida={disponiveis.find((janela) => janela.id === escolhida)?.name ?? null}
      />
    </Secao>
  );
}

// ── conflitos já registrados ───────────────────────────────────────────────

function ConflitosDaMudanca({ mudanca }: { mudanca: Mudanca }) {
  if (mudanca.changeWindowId === null) return null;
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold">Na janela em que está agendada</h4>
      <ListaDeConflitos
        conflitos={mudanca.conflicts}
        vazio="Nada colide com esta mudança na janela atual."
        prefixo="Hoje:"
      />
    </div>
  );
}

function ListaDeConflitos({
  conflitos,
  vazio,
  prefixo,
}: {
  conflitos: readonly Conflito[];
  vazio: string;
  prefixo: string;
}) {
  if (conflitos.length === 0) {
    return <p className="text-sm text-muted-foreground">{vazio}</p>;
  }
  return (
    <div className="space-y-2">
      {conflitos.map((conflito, indice) => (
        <AvisoDeConflito
          key={`${conflito.tipo}-${conflito.comTicketId ?? conflito.comBlackoutId ?? indice}`}
          gravidade={conflito.gravidade}
          titulo={`${prefixo} ${ROTULO_DE_TIPO_DE_CONFLITO[conflito.tipo].toLowerCase()}`}
        >
          <p className="text-sm">{conflito.mensagem}</p>
          <p className="text-xs opacity-80">
            {formatarDataHora(new Date(conflito.sobreposicao.inicio).toISOString())} →{' '}
            {formatarDataHora(new Date(conflito.sobreposicao.fim).toISOString())}
            {conflito.recursos.length > 0
              ? ` · ${conflito.recursos.length} recurso(s) em comum`
              : ''}
          </p>
        </AvisoDeConflito>
      ))}
    </div>
  );
}

// ── exceção de congelamento — critério 4.12 ────────────────────────────────

/**
 * Pedir a exceção, e ver o pedido sendo decidido.
 *
 * ★ Não há motor próprio: o pedido abre uma `ApprovalRequest` com a política
 * `blackout-exception`, de quórum superior, e o acompanhamento é o mesmo painel
 * de aprovação da Fase 3. A trilha — quem, quando, de qual endereço, sobre qual
 * versão do registro — vale para furar um congelamento exatamente como vale
 * para o comitê.
 *
 * `bloqueiosDaPrevia` é o que a prévia da janela escolhida devolveu — ver a
 * nota do cabeçalho do arquivo. Sem ele, a mudança ainda sem janela nunca
 * chegaria a este formulário.
 */
function ExcecoesDeCongelamento({
  mudanca,
  bloqueiosDaPrevia,
  nomeDaJanelaEscolhida,
}: {
  mudanca: Mudanca;
  bloqueiosDaPrevia: readonly Conflito[];
  nomeDaJanelaEscolhida: string | null;
}) {
  const congelamentos = useQuery({
    queryKey: chavesDeMudanca.congelamentos,
    queryFn: listarCongelamentos,
    retry: false,
  });

  const bloqueando = congelamentosBloqueando(mudanca.blocking, bloqueiosDaPrevia);
  /**
   * Quais desses vieram da janela escolhida no combo.
   *
   * Só para eles o formulário pode dizer "é a janela X que cai dentro dele" —
   * um congelamento que veio de `mudanca.blocking` fala da janela em que a
   * mudança **já está**, e nomear a do combo ali seria mentir com precisão.
   */
  const daPrevia = new Set(congelamentosBloqueando(bloqueiosDaPrevia));

  const porId = new Map((congelamentos.data ?? []).map((c) => [c.id, c]));
  const pedidas = mudanca.blackoutExceptions;
  const semExcecao = congelamentosSemExcecao(bloqueando, pedidas);

  if (bloqueando.length === 0 && pedidas.length === 0) return null;

  return (
    <div className="space-y-3 border-t border-border pt-4">
      <h4 className="flex items-center gap-2 text-sm font-semibold">
        <Snowflake className="h-4 w-4 text-destructive" aria-hidden />
        Exceção de congelamento
      </h4>

      {semExcecao.map((id) => (
        <FormularioDeExcecao
          key={id}
          mudanca={mudanca}
          blackoutId={id}
          congelamento={porId.get(id)}
          janela={
            daPrevia.has(id)
              ? { nome: nomeDaJanelaEscolhida, origem: 'escolhida' }
              : { nome: mudanca.changeWindow?.name ?? null, origem: 'atual' }
          }
        />
      ))}

      {pedidas.map((excecao) => (
        <div key={excecao.id} className="space-y-2 rounded-md border border-border px-3 py-2.5">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <EtiquetaDeExcecao situacao={excecao.status} />
            <span className="font-medium">
              {excecao.blackoutPeriod?.name ?? porId.get(excecao.blackoutPeriodId)?.name ?? 'Congelamento'}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">“{excecao.justification}”</p>
          <DecisorDaExcecao excecao={excecao} />
          {excecao.approvalRequestId ? (
            <AprovacaoPorId
              approvalRequestId={excecao.approvalRequestId}
              ticketId={mudanca.ticketId}
            />
          ) : (
            <p className="text-xs text-muted-foreground">
              Sem aprovação vinculada — o pedido foi registrado antes de a política existir.
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

function DecisorDaExcecao({ excecao }: { excecao: Mudanca['blackoutExceptions'][number] }) {
  const pessoas = useDicionarioDePessoas([excecao.requestedById, excecao.decidedById]);
  return (
    <p className="text-xs text-muted-foreground">
      Pedida por <NomeDePessoa id={excecao.requestedById} pessoas={pessoas} />{' '}
      <span title={formatarDataHora(excecao.requestedAt)}>
        em {formatarDataHora(excecao.requestedAt)}
      </span>
      {excecao.decidedAt ? (
        <>
          {' '}· decidida por <NomeDePessoa id={excecao.decidedById} pessoas={pessoas} /> em{' '}
          {formatarDataHora(excecao.decidedAt)}
        </>
      ) : null}
    </p>
  );
}

const MINIMO_DA_JUSTIFICATIVA = 20;

function FormularioDeExcecao({
  mudanca,
  blackoutId,
  congelamento,
  janela,
}: {
  mudanca: Mudanca;
  blackoutId: string;
  congelamento: Congelamento | undefined;
  /** Qual janela caiu no congelamento: a escolhida no combo ou a de agora. */
  janela: { nome: string | null; origem: 'escolhida' | 'atual' };
}) {
  const cliente = useQueryClient();
  const [justificativa, setJustificativa] = useState('');
  const [falha, setFalha] = useState<Falha | null>(null);

  const pedir = useMutation({
    mutationFn: () =>
      pedirExcecaoDeCongelamento(mudanca.ticketId, {
        blackoutPeriodId: blackoutId,
        justification: justificativa.trim(),
      }),
    onMutate: () => setFalha(null),
    onSuccess: () => {
      setJustificativa('');
      void cliente.invalidateQueries({ queryKey: chaves.raiz });
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  const curta = justificativa.trim().length < MINIMO_DA_JUSTIFICATIVA;

  return (
    <form
      className="space-y-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2.5"
      onSubmit={(evento) => {
        evento.preventDefault();
        if (!curta) pedir.mutate();
      }}
    >
      <p className="text-sm font-medium text-destructive">
        {congelamento
          ? `Bloqueada pelo congelamento “${congelamento.name}” — ${congelamento.reason}`
          : 'Bloqueada por um congelamento.'}
      </p>
      {janela.nome ? (
        // Qual janela produziu o bloqueio: sem isto, quem experimentou três
        // janelas no combo não sabe sobre qual delas está pedindo a exceção.
        <p className="text-xs text-destructive/90">
          {janela.origem === 'escolhida' ? (
            <>
              É a janela <strong>{janela.nome}</strong>, escolhida acima, que cai dentro dele. A
              exceção aprovada libera o agendamento nela.
            </>
          ) : (
            <>
              É a janela <strong>{janela.nome}</strong>, em que a mudança já está, que cai dentro
              dele.
            </>
          )}
        </p>
      ) : null}
      <Campo
        rotulo="Justificativa do pedido"
        htmlFor={`excecao-${blackoutId}`}
        dica={`Pelo menos ${MINIMO_DA_JUSTIFICATIVA} caracteres. Sem mínimo, a justificativa seria “ok” — e uma exceção justificada com “ok” é uma exceção que ninguém consegue revisar depois.`}
      >
        <textarea
          id={`excecao-${blackoutId}`}
          rows={3}
          value={justificativa}
          onChange={(evento) => setJustificativa(evento.target.value)}
          className={CLASSE_DE_CAMPO}
        />
      </Campo>

      {falha ? (
        <AvisoDeFalha
          falha={falha}
          aoRecarregar={() =>
            void cliente.refetchQueries({ queryKey: chaves.item(mudanca.ticketId) })
          }
        />
      ) : null}

      <div className="flex items-center gap-3">
        <Button type="submit" size="sm" variant="danger" loading={pedir.isPending} disabled={curta}>
          Pedir exceção
        </Button>
        <span className="text-xs text-muted-foreground">
          Abre uma aprovação de <strong>nível superior</strong> — quem autoriza furar um
          congelamento não é quem aprova a mudança.
        </span>
      </div>
    </form>
  );
}
